import { useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  ArrowRight,
  FileText,
  UserPlus,
  Truck,
  Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { FeedbackMessage } from "@/components/ui/feedback";
import { Input, Label, Textarea, FieldError } from "@/components/ui/input";
import { Seo } from "@/components/Seo";
import { invokeFunction, ApiError } from "@/lib/api";
import { contactSchema, type ContactInput } from "@/features/forms/schemas";
import { createLocalContactMessage } from "@/features/contact/repo";
import { company, addressText } from "@/content/site";

const departments = [
  { icon: FileText, title: "Quotes & new freight", body: "Pricing, capacity, and lane setup.", to: "#contact-form", cta: "Send a request" },
  { icon: UserPlus, title: "Driver recruiting", body: "Company driver jobs and hiring questions.", to: "/careers", cta: "See careers" },
  { icon: Truck, title: "Owner-operators", body: "Run your truck with steady freight.", to: "/apply?type=carrier", cta: "Start onboarding" },
  { icon: Search, title: "Track a shipment", body: "Live status, location, and ETA.", to: "/track", cta: "Track now" },
];

const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(addressText)}&output=embed`;

export default function Contact() {
  const [sent, setSent] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ContactInput>({ resolver: zodResolver(contactSchema) });

  async function onSubmit(values: ContactInput) {
    setServerError(null);
    try {
      await invokeFunction("contact", values);
      setSent(true);
    } catch (err) {
      // Backend not wired yet (Phase 2/3) - treat as soft success in dev.
      if (err instanceof ApiError && err.status === 503) {
        await createLocalContactMessage(values);
        setSent(true);
        return;
      }
      setServerError("Something went wrong. Please call us instead.");
    }
  }

  return (
    <>
      <Seo title={`Contact | ${company.name}`} description="Get a quote, set up a lane, or reach our dispatch team. Bull and Bear Co - Aurora, IL." />

      {/* HERO */}
      <section className="relative overflow-hidden bg-brand-900 text-white">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.06]"
          style={{ backgroundImage: "repeating-linear-gradient(45deg, #fff 0 1px, transparent 1px 10px)" }}
        />
        <div className="pointer-events-none absolute -right-40 -top-40 h-[440px] w-[440px] rounded-full bg-accent-500/15 blur-3xl" />
        <div className="relative mx-auto max-w-[1200px] px-6 py-16 lg:py-20">
          <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-accent-400">Contact</div>
          <h1 className="mb-4 text-[clamp(2rem,4vw,3rem)] font-bold leading-[1.05]">Let's move it right.</h1>
          <p className="max-w-xl text-lg text-brand-200">
            Tell us what you need to move and we'll get back to you fast - usually the same business day.
          </p>
        </div>
      </section>

      {/* DEPARTMENT ROUTING */}
      <section className="border-b border-brand-100 bg-brand-50 py-12">
        <div className="mx-auto max-w-[1200px] px-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {departments.map((d) => {
              const inner = (
                <>
                  <d.icon className="mb-3 h-6 w-6 text-accent-500" />
                  <h2 className="font-semibold text-brand-900">{d.title}</h2>
                  <p className="mb-3 text-sm text-brand-500">{d.body}</p>
                  <span className="mt-auto inline-flex items-center text-sm font-semibold text-accent-600">
                    {d.cta} <ArrowRight className="ml-1 h-4 w-4" />
                  </span>
                </>
              );
              const className =
                "group flex h-full flex-col rounded-xl border border-brand-100 bg-white p-5 transition-all hover:border-accent-500 hover:shadow-md";
              return d.to.startsWith("#") ? (
                <a key={d.title} href={d.to} className={className}>{inner}</a>
              ) : (
                <Link key={d.title} to={d.to} className={className}>{inner}</Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* FORM + INFO */}
      <section className="mx-auto grid max-w-[1200px] gap-10 px-6 py-16 lg:grid-cols-[1fr_340px] lg:py-20">
        <div id="contact-form" className="scroll-mt-24">
          <h2 className="mb-6 text-2xl font-bold text-brand-900">Send us a message</h2>
          {sent ? (
            <FeedbackMessage tone="success">
              Thanks - message received. We'll be in touch shortly, usually the same business day.
            </FeedbackMessage>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
              <div>
                <Label htmlFor="name">Name</Label>
                <Input id="name" {...register("name")} aria-invalid={!!errors.name} />
                <FieldError>{errors.name?.message}</FieldError>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" {...register("email")} aria-invalid={!!errors.email} />
                  <FieldError>{errors.email?.message}</FieldError>
                </div>
                <div>
                  <Label htmlFor="phone">Phone (optional)</Label>
                  <Input id="phone" {...register("phone")} />
                </div>
              </div>
              <div>
                <Label htmlFor="message">Message</Label>
                <Textarea id="message" {...register("message")} aria-invalid={!!errors.message} />
                <FieldError>{errors.message?.message}</FieldError>
              </div>

              {/* Honeypot - visually hidden, must stay empty */}
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                className="hidden"
                aria-hidden="true"
                {...register("company_website")}
              />

              <FeedbackMessage tone="error">{serverError}</FeedbackMessage>

              <Button type="submit" variant="accent" size="lg" className="w-full sm:w-auto" disabled={isSubmitting}>
                {isSubmitting ? "Sending..." : "Send message"}
              </Button>
            </form>
          )}
        </div>

        <aside className="space-y-6">
          <div className="rounded-xl border border-brand-100 bg-brand-50 p-6">
            <h3 className="mb-4 font-semibold text-brand-900">Reach us</h3>
            <div className="space-y-4 text-sm text-brand-700">
              <div className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-accent-500" />
                <span>
                  {company.address.line1}
                  <br />
                  {company.address.city}, {company.address.state} {company.address.zip}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="h-5 w-5 shrink-0 text-accent-500" />
                <a href={`tel:${company.phone.replace(/[^+\d]/g, "")}`} className="hover:underline">{company.phone}</a>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 shrink-0 text-accent-500" />
                <a href={`mailto:${company.email}`} className="break-all hover:underline">{company.email}</a>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-brand-100 bg-brand-50 p-6">
            <h3 className="mb-4 flex items-center gap-2 font-semibold text-brand-900">
              <Clock className="h-5 w-5 text-accent-500" /> Hours
            </h3>
            <dl className="space-y-2 text-sm text-brand-700">
              <div className="flex justify-between">
                <dt>Dispatch</dt>
                <dd className="font-medium">24 / 7</dd>
              </div>
              <div className="flex justify-between">
                <dt>Office (Mon-Fri)</dt>
                <dd className="font-medium">8:00a - 5:00p CT</dd>
              </div>
              <div className="flex justify-between">
                <dt>Weekend office</dt>
                <dd className="font-medium">By appointment</dd>
              </div>
            </dl>
          </div>
        </aside>
      </section>

      {/* MAP */}
      <section className="border-t border-brand-100">
        <iframe
          title={`Map to ${company.name}`}
          src={mapSrc}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          className="h-[360px] w-full border-0 grayscale-[35%]"
        />
      </section>
    </>
  );
}
